import type { ReactNode } from 'react'
import { Platform, StyleSheet, Text, View } from 'react-native'
import { colors, fontWeight } from '../tokens'

type Props = {
  number: 1 | 2 | 3
  done: boolean
  isLast?: boolean
  title: string
  description: ReactNode
  children?: ReactNode
}

const Check = () =>
  Platform.OS === 'web' ? (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color: '#fff' }}
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ) : (
    <Text style={{ color: '#fff', fontWeight: '800' }}>✓</Text>
  )

// Single numbered row in the stepper. Number circle flips from soft-indigo
// "N" to mint with a white check when done; the rail line below it fills
// mint to visually thread one completed step to the next.
export const FulfilmentStep = ({ number, done, isLast, title, description, children }: Props) => (
  <View style={[styles.row, !isLast && styles.notLast]}>
    <View style={styles.rail}>
      <View style={[styles.circle, done && styles.circleDone]}>
        {done ? <Check /> : <Text style={styles.circleNum}>{number}</Text>}
      </View>
      {!isLast ? <View style={[styles.line, done && styles.lineDone]} /> : null}
    </View>
    <View style={styles.body}>
      <Text style={styles.title}>{title}</Text>
      <View style={styles.desc}>{description}</View>
      {children}
    </View>
  </View>
)

const styles = StyleSheet.create({
  row: { flexDirection: 'row', gap: 20, paddingVertical: 26 },
  notLast: { borderBottomWidth: 1, borderBottomColor: colors.border },
  rail: { width: 40, alignItems: 'center' },
  circle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary[100],
    alignItems: 'center',
    justifyContent: 'center',
  },
  circleDone: { backgroundColor: colors.positive },
  circleNum: {
    fontSize: 16,
    fontWeight: fontWeight.bold,
    color: colors.primary[700],
  },
  line: {
    width: 2,
    flex: 1,
    marginTop: 8,
    backgroundColor: colors.border,
    borderRadius: 2,
  },
  lineDone: { backgroundColor: colors.positive },
  body: { flex: 1, paddingBottom: 2, minWidth: 0 },
  title: {
    fontSize: 17.5,
    fontWeight: fontWeight.bold,
    color: colors.ink,
    marginBottom: 6,
    marginTop: 6,
    letterSpacing: -0.18,
  },
  desc: { marginBottom: 16 },
})
